import React from "react";
import { Helmet } from "react-helmet-async";
import { gtag } from "../utils/gtag";
import { MAILTO_URL } from "../utils/constants";
import { SiteImages } from "../utils/imagePaths";
import "./About.css";

const About = () => {
  return (
    <div className="about-container">
      <Helmet>
        <title>About Us | Blossom and Graze Bristol</title>
        <meta name="description" content="Learn about Blossom and Graze Bristol - our story, values, and passion for artisanal grazing boards and event catering. We source the finest local Bristol ingredients to create unforgettable food experiences." />
        <link rel="canonical" href="https://www.blossomandgraze.co.uk/about" />
        <meta property="og:url" content="https://www.blossomandgraze.co.uk/about" />
        <meta property="og:title" content="About Us | Blossom and Graze Bristol" />
        <meta property="og:description" content="Learn about Blossom and Graze Bristol - our story, values, and passion for artisanal grazing boards and event catering in Bristol." />
      </Helmet>
      {/* Hero */}
      <section className="about-hero">
        <div className="about-hero-bg">
          <img
            src={SiteImages.aboutHero}
            alt="Artisanal afternoon tea spread with sandwiches, scones and sweet treats"
          />
        </div>
        <div className="about-hero-overlay" />
        <div className="about-hero-text">
          <h1>Our Story</h1>
          <div className="about-hero-divider" />
        </div>
      </section>

      {/* Section 1: A Passion */}
      <section className="story-section-1">
        <div className="story-grid">
          <div className="story-text">
            <span className="section-label">The Beginning</span>
            <h2>A Passion for Bringing People Together</h2>
            <div className="story-body">
              <p>
                Growing up in a big family, some of my fondest memories were
                created around the dining table. Whether it was Sunday lunches,
                birthday celebrations, or impromptu gatherings, I learned early
                on that food has a magical way of bringing people together.
              </p>
              <p>
                My love for creating beautiful, abundant spreads started in
                these moments. I would spend hours arranging platters, making
                sure each element not only tasted delicious but looked stunning
                too. The joy on people's faces when they saw the spread was
                always the best reward.
              </p>
            </div>
          </div>
          <div className="story-image-wrapper">
            <img
              src={SiteImages.aboutStory1}
              alt="Grazing table with cheeses, charcuterie and seasonal accompaniments"
            />
            <div className="story-image-accent" />
          </div>
        </div>
      </section>

      {/* Section 2: From Family Table */}
      <section className="story-section-2">
        <div className="story-inner">
          <div className="story-grid">
            <div className="story-image-wrapper">
              <img
                src={SiteImages.aboutStory2}
                alt="Balloon hoop display for a celebration or event"
              />
            </div>
            <div className="story-text-col">
              <h2>From Family Table to Blossom and Graze</h2>
              <div className="story-body">
                <p>
                  What started as a passion for creating memorable family
                  gatherings has blossomed into something more. Friends and
                  family would always comment on how beautiful my spreads looked,
                  encouraging me to share this gift with others. That&apos;s how
                  Blossom and Graze was born &ndash; from a genuine love of
                  bringing people together over beautifully presented food.
                </p>
                <p>
                  Today, I bring that same attention to detail and love for
                  creativity to every grazing board I create. Each spread is
                  thoughtfully curated, combining premium ingredients with
                  artistic presentation to create an experience that&apos;s both
                  delicious and visually stunning.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <div className="philosophy-header">
          <span className="section-label">Values &amp; Origins</span>
          <h2>Our Philosophy</h2>
        </div>
        <div className="philosophy-grid">
          <div className="philosophy-card">
            <span className="material-symbols-outlined philosophy-icon">
              local_mall
            </span>
            <h3>Bristol Roots</h3>
            <p>
              At Blossom and Graze, we believe eating should be an experience
              that delights all the senses. Wherever possible, we bake fresh
              in-house and carefully select the best-tasting ingredients to
              create food that looks beautiful and tastes even better.
            </p>
          </div>
          <div className="philosophy-card">
            <span className="material-symbols-outlined philosophy-icon">
              brush
            </span>
            <h3>Artisanal Presentation</h3>
            <p>
              We treat every platter as a canvas. Our aesthetic is guided by
              the seasons &mdash; structured yet effortless, and always
              incredibly inviting. Every detail is considered with artistic
              flair.
            </p>
          </div>
          <div className="philosophy-card">
            <span className="material-symbols-outlined philosophy-icon">
              eco
            </span>
            <h3>Botanical Flavors</h3>
            <p>
              Whether it&apos;s an intimate afternoon tea, a corporate event,
              or a wedding celebration, we pour our heart into every detail,
              creating grazing experiences that make moments truly special.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <p>Let&apos;s create something beautiful together.</p>
        <a href={MAILTO_URL} className="cta-button" onClick={() => {
          gtag("event", "enquire_click", {
            link_location: "about_cta",
          });
        }}>
          Get in Touch
        </a>
      </section>
    </div>
  );
};

export default About;

